import React, { LabelHTMLAttributes, HTMLAttributes, PropsWithChildren, ReactNode } from 'react'
import uniqueId from 'lodash.uniqueid'
import clsx from 'clsx'
import { FormControl as FormControlStyles } from '@airbus/elements/elements'
import { createUseStyles } from '../../lib/styling'
import useDeprecatedComponent from '../../lib/hooks/useDeprecatedComponent'
import useDeprecatedProps from '../../lib/hooks/useDeprecatedProps'
import Link from '../Link'
import { CommonProps } from '../helperTypes'

type Props = PropsWithChildren<{
  /** Specify the label for screen readers */
  'aria-label'?: string
  /** Error state flag */
  error?: boolean
  /** Error message */
  errorText?: string
  /** help link label */
  helpLinkLabel?: string
  /** Help text below input */
  helpText?: string
  /**
   * The id of the control.
   * Use this property to make `label` and `helpText` accessible for screen readers.
   */
  id?: string
  /** The label of the control */
  label?: string
  /** Adornment component to display near the label */
  labelAdornment?: ReactNode
  /**
   * @param {MouseEvent<HTMLAnchorElement>} event React's original MouseEvent.
   */
  onHelpLinkClick?(event: React.MouseEvent<HTMLAnchorElement>): void
  /** The placeholder to display */
  placeholder?: string
  /** If `true`, the control is mandatory. */
  required?: boolean
  /** Success state flag */
  success?: boolean
  /** Success message */
  successText?: string
}>

export type Deprecatedprops = {
  helperText?: string
}

export type FormControlProps = Props & Deprecatedprops & CommonProps

const useStyles = createUseStyles(FormControlStyles, 'formcontrol')

const handleDeprecatedProps = (props: FormControlProps) => {
  const { helpText, helperText, ...rest } = props
  return {
    helpText: helpText || helperText,
    ...rest,
  }
}

const FormControl = (props: FormControlProps) => {
  const {
    'aria-label': ariaLabel,
    children,
    className,
    error,
    errorText,
    helpLinkLabel,
    helpText,
    label,
    labelAdornment,
    onHelpLinkClick,
    placeholder,
    required,
    success,
    successText,
    ...rest
  } = handleDeprecatedProps(props)
  useDeprecatedProps('FormControl', { helperText: 'helpText' }, props)
  const idRef = React.useRef<string>(uniqueId('FormControl-'))
  const id = rest.id || idRef.current
  const classes = useStyles()

  if (!children) return null
  const control = React.Children.only(children)
  if (!React.isValidElement(control)) {
    return null
  }

  const controlProps = {
    id,
    required,
    placeholder: placeholder,
    'aria-labelledby': label ? `${id}-label` : undefined,
    'aria-label': ariaLabel,
    className: classes.control,
    valid: success,
    invalid: error,
  }
  const controlChild = React.cloneElement<any>(control, controlProps)
  const hasHelp = (error && errorText) || (success && successText) || helpText

  return (
    <div
      className={clsx(className, classes.root, error && classes['_error'], success && classes['_success'])}
      {...rest}
    >
      {label && (
        <FormControlLabel htmlFor={id} id={`${id}-label`}>
          {label}
          {!required && <div className={classes.optional}>(Optional)</div>}
          {labelAdornment && <div className={classes.info}>{labelAdornment}</div>}
        </FormControlLabel>
      )}

      <div className={classes.control}>{controlChild}</div>

      {hasHelp && (
        <FormControlHelptext>
          {error ? errorText : success ? successText : helpText}
          {helpLinkLabel && (
            <div className={classes.helplink}>
              <Link size='xsmall' variant='underlined' tabIndex={0} onClick={onHelpLinkClick}>
                {helpLinkLabel}
              </Link>
            </div>
          )}
        </FormControlHelptext>
      )}
    </div>
  )
}
export default FormControl

export const FormControlLabel = ({ children, ...rest }: PropsWithChildren<LabelHTMLAttributes<HTMLLabelElement>>) => {
  const classes = useStyles()
  return (
    <label className={clsx(classes.label)} {...rest}>
      {children}
    </label>
  )
}

export const FormControlHelptext = ({ children, ...rest }: PropsWithChildren<HTMLAttributes<HTMLDivElement>>) => {
  const classes = useStyles()
  return (
    <div className={clsx(classes.help)} {...rest}>
      {children}
    </div>
  )
}

/** RETROCOMPAT */
// retrocompat : export old "Field" component name
export const Field = (props: FormControlProps) => {
  useDeprecatedComponent('Field', 'Please use FormControl.')
  return <FormControl {...props} />
}
// retrocompat : export old "FieldLabel" component name
export const FieldLabel = (props: PropsWithChildren<LabelHTMLAttributes<HTMLLabelElement>>) => {
  useDeprecatedComponent('FieldLabel', 'Please use FormControlLabel.')
  return <FormControlLabel {...props} />
}
// retrocompat : export old "FieldHelptext" component name
export const FieldHelptext = (props: PropsWithChildren<HTMLAttributes<HTMLDivElement>>) => {
  useDeprecatedComponent('FieldHelptext', 'Please use FormControlHelptext.')
  return <FormControlHelptext {...props} />
}
